import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Building2,
  Kanban,
  FileText,
  Eye,
  Send,
  Share2,
  TrendingUp,
  Settings,
} from 'lucide-react';
import { AppShell, type NavSection } from '@solid/ui';
import { OrgSwitcher } from './OrgSwitcher';

/**
 * The one page frame, from @solid/ui. The hub app rail offsets <body> itself,
 * so nothing here adds a rail inset. Settings is also reachable from the rail's
 * bottom nav (registered below), but the shell must offer a way in on its own.
 */
const NAV: NavSection[] = [
  { items: [{ to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true }] },
  {
    label: 'Sales',
    items: [
      { to: '/admin/deals', label: 'Deals', icon: Kanban },
      { to: '/admin/sequences', label: 'Sequences', icon: Send },
    ],
  },
  {
    label: 'Marketing',
    items: [
      { to: '/admin/forms', label: 'Forms', icon: FileText },
      { to: '/admin/visitors', label: 'Visitors', icon: Eye },
      { to: '/admin/social', label: 'Social', icon: Share2 },
    ],
  },
  {
    label: 'Contacts',
    items: [
      { to: '/admin/contacts', label: 'Contacts', icon: Users },
      { to: '/admin/organizations', label: 'Organizations', icon: Building2 },
    ],
  },
  {
    label: 'Workspace',
    items: [{ to: '/admin/settings', label: 'Settings', icon: Settings }],
  },
];

/** Real screens that are not nav items still get a name in the topbar. */
const OFF_NAV_TITLES: Array<[string, string]> = [
  ['/admin/social/templates', 'Social Templates'],
];

function useAppRailExtensions() {
  const navigate = useNavigate();

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    let interval: ReturnType<typeof setInterval>;

    const register = () => {
      if (!window.SolidHub?.isInitialized?.() || !window.SolidHub.extensions) return false;

      cleanup = window.SolidHub.extensions.registerBottomNav({
        id: 'sales-settings',
        label: 'Settings',
        icon: 'Settings',
        tooltip: 'Settings',
        onClick: () => navigate('/admin/settings'),
      });
      return true;
    };

    if (!register()) {
      interval = setInterval(() => {
        if (register()) clearInterval(interval);
      }, 200);
    }

    return () => {
      clearInterval(interval);
      cleanup?.();
    };
  }, [navigate]);
}

export function AppLayout() {
  useAppRailExtensions();

  return (
    <AppShell
      brand={{ name: 'Solid Sales', mark: <TrendingUp /> }}
      sections={NAV}
      offNavTitles={OFF_NAV_TITLES}
      topbarEnd={<OrgSwitcher />}
    >
      <Outlet />
    </AppShell>
  );
}
